/**
 * Order actions renderer
 */
import { Component, ViewEncapsulation } from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import {
  ActionButton,
  ENTITY_TYPES,
  IOrderInventory,
  COMMON_ACTION_TYPES,
} from '@shared/types';
import { ORDER_STATUS_VALUE } from '@shared/types/enums/order-status.enum';
import { Router } from '@angular/router';
import { cloneDeep, forEach, get, includes, isEmpty } from 'lodash';
import { OrdersService, StateService } from '@shared/services';
import { APIActions, ToolsActions, UiActions } from '@store/actions';
import { ORDER_ACTION_BUTTONS } from '@shared/types/entities/order/order-action-buttons.enum';
import { getSingularEntityName } from '@shared/configs';
import { PdfBaseService } from '@app/shared/common/make-pdf';
import { PdfGenerateServiceV2 } from '@app/shared/common/make-pdf/v2';
import { buildOrderPdfConfig } from '@app/shared/services/orders/order-pdf-config';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-order-actions-renderer',
  templateUrl: 'order-actions-renderer.component.html',
  styleUrls: ['order-actions-renderer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XVIOrderRendererComponent implements AgRendererComponent {
  public data: IOrderInventory;
  public actionButtons: ActionButton[];

  // eslint-disable-next-line max-params
  public constructor(
    public toolsActions: ToolsActions,
    public orderService: OrdersService,
    public pdfBaseService: PdfBaseService,
    public pdfGenerateService: PdfGenerateServiceV2,
    private readonly router: Router,
    private readonly uiActions: UiActions,
    private readonly ordersService: OrdersService,
    private readonly stateService: StateService,
    private readonly apiActions: APIActions,
    private readonly i18nService: XVII18nService,
  ) {}

  public agInit(params: ICellRendererParams): void {
    this.data = params.node.data;
    this.initActionButtons();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: any): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };
    const entityType = this.ordersService.config.ENTITY_TYPE;
    const previousRequest = this.stateService.getByPath(
      ['entities', entityType, 'previousRequest'],
      defaultRequestParams,
    );

    this.apiActions.fetchList(
      entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  public edit(): void {
    const status = this.data?.status;

    this.router.navigate(
      [this.ordersService.config.uiRoute, get(this, 'data._id', '')],
      {
        queryParams: {
          get_updated_items: includes(
            this.orderService.needGetUpdateItemsStatus,
            status,
          )
            ? true
            : null,
        },
      },
    );
  }

  public async submit(): Promise<boolean> {
    const currentDocument = await this.ordersService.fetchGetDocumentById(
      this.data._id,
    );
    if (
      isEmpty(currentDocument.updated_items) &&
      isEmpty(currentDocument.unavailable_items)
    ) {
      const result = await this.uiActions.confirm({
        text: this.i18nService.instant(
          I18n.ConfirmMessages.CanNotChangeAfterSubmitted,
          {
            entityType: this.i18nService.instant(
              getSingularEntityName(ENTITY_TYPES.ORDER),
            ),
          },
        ),
      });
      if (!result.confirmed) {
        return false;
      }
      await this.changeOrderStatusById(ORDER_STATUS_VALUE.Pending);

      return true;
    }
    this.edit();

    return true;
  }

  public async cancelOrder(): Promise<boolean> {
    const currentDocument = await this.ordersService.fetchGetDocumentById(
      this.data._id,
      {
        query: { get_updated_items: false },
      },
    );
    if (
      !isEmpty(currentDocument.updated_items) ||
      !isEmpty(currentDocument.unavailable_items)
    ) {
      await this.ordersService.acknowledgeDocument(this.data._id);
    }

    const message = this.i18nService.instant(
      I18n.ConfirmMessages.CancelEntity,
      {
        entityType: this.i18nService.instant(
          getSingularEntityName(ENTITY_TYPES.ORDER),
        ),
      },
    );

    const result = await this.uiActions.confirm({
      text: message,
    });
    if (!result.confirmed) {
      return false;
    }
    await this.changeOrderStatusById(ORDER_STATUS_VALUE.Canceled);
  }

  public async rejectAll(): Promise<boolean> {
    const result = await this.uiActions.confirm({
      text: I18n.ConfirmMessages.RejectEntireOrder,
    });
    if (!result.confirmed) {
      return false;
    }
    await this.changeOrderStatusById(ORDER_STATUS_VALUE.Rejected);
  }

  public async changeOrderStatusById(
    status: ORDER_STATUS_VALUE,
  ): Promise<void> {
    await this.apiActions.updateEntityAsPromised(
      ENTITY_TYPES.ORDER,
      { status },
      { endpoint: this.data._id },
    );
    this.refresh({});
  }

  public emitAction(type: string): void {
    switch (type) {
      case COMMON_ACTION_TYPES.View:
      case COMMON_ACTION_TYPES.Edit:
      case COMMON_ACTION_TYPES.Accept:
        this.edit();
        break;
      case COMMON_ACTION_TYPES.Submit:
        this.submit();
        break;
      case COMMON_ACTION_TYPES.Cancel:
        this.cancelOrder();
        break;
      case COMMON_ACTION_TYPES.Reject:
        this.rejectAll();
        break;
      case COMMON_ACTION_TYPES.Print:
        this.print();
        break;
      case COMMON_ACTION_TYPES.Export:
        this.export();
        break;
      default:
        break;
    }
  }

  public async print(): Promise<void> {
    const isOpenOrCanceled = includes(
      [ORDER_STATUS_VALUE.Canceled, ORDER_STATUS_VALUE.Open],
      this.data.status,
    );

    const pdfConfig = buildOrderPdfConfig(
      () => this.orderService.getPdfData(this.data),
      !isOpenOrCanceled,
      null,
      true,
    );

    await this.pdfGenerateService.printPdfAsync(pdfConfig);
  }

  public async export(): Promise<void> {
    const isOpenOrCanceled = includes(
      [ORDER_STATUS_VALUE.Canceled, ORDER_STATUS_VALUE.Open],
      this.data.status,
    );

    const pdfConfig = buildOrderPdfConfig(
      () => this.orderService.getPdfData(this.data),
      !isOpenOrCanceled,
      null,
      true,
    );

    await this.pdfGenerateService.downloadPdfAsync(pdfConfig);
  }

  private initActionButtons(): void {
    this.actionButtons = cloneDeep(ORDER_ACTION_BUTTONS) as ActionButton[];
    forEach(this.actionButtons, (btn) => {
      switch (btn.type) {
        case COMMON_ACTION_TYPES.View:
          btn.visible = includes(
            [
              ORDER_STATUS_VALUE.Completed,
              ORDER_STATUS_VALUE.Canceled,
              ORDER_STATUS_VALUE.Rejected,
            ],
            this.data.status,
          );
          break;
        case COMMON_ACTION_TYPES.Edit:
        case COMMON_ACTION_TYPES.Submit:
        case COMMON_ACTION_TYPES.Cancel:
          btn.visible = ORDER_STATUS_VALUE.Open === this.data.status;
          break;
        case COMMON_ACTION_TYPES.Print:
        case COMMON_ACTION_TYPES.Export:
          btn.visible = true;
          break;
        case COMMON_ACTION_TYPES.Accept:
        case COMMON_ACTION_TYPES.Reject:
          btn.visible = ORDER_STATUS_VALUE.Pending === this.data.status;

          return;
        default:
          break;
      }
    });
  }
}
